/**
 * Reasons an order earns optimization billing, split by what each reason is
 * intrinsically about: a population of checkout traffic, or a single order.
 */

/**
 * Properties of the checkout-tree leaf a visitor landed on — what attestations
 * assert and exclusions suppress. Mirrors the CRDB enum of the same name.
 */
export enum PopulationQualifierKind {
  /** A concluded split proved this population beat what it replaced. */
  PROVEN_LIFT = "proven_lift",
  /** The leaf's checkout experience serves a custom rate table. */
  CUSTOM_RATE_TABLE = "custom_rate_table",
  /**
   * The leaf is reached through a segment Redo offers and Shopify does not,
   * routing traffic somewhere the merchant could not have routed it alone.
   */
  REDO_ONLY_SEGMENTATION = "redo_only_segmentation",
  /** The leaf's checkout experience runs a live shipping-AI model. */
  SHIPPING_AI = "shipping_ai",
}

/**
 * Qualifiers decided by what an individual order did, merged in per order at
 * stamp time. Never stored as rows — the only lever on them is their rate.
 */
export enum OrderQualifierKind {
  /**
   * The order used Redo shipping methods. The broadest kind — price it below
   * the others so it only bills where nothing else does.
   */
  REDO_SHIPPING_METHODS = "redo_shipping_methods",
  /** The order used Redo checkout widgets. */
  WIDGETS = "widgets",
  /** The order used a Redo post-purchase upsell. */
  POST_PURCHASE_UPSELL = "post_purchase_upsell",
  /** The order used the Redo intent system. */
  INTENT = "intent",
}

export type CheckoutOptimizationQualifierKind =
  | PopulationQualifierKind
  | OrderQualifierKind;

export const CHECKOUT_OPTIMIZATION_QUALIFIER_KINDS: readonly CheckoutOptimizationQualifierKind[] =
  [
    ...Object.values(PopulationQualifierKind),
    ...Object.values(OrderQualifierKind),
  ];
